export enum MuscleGroup {
  CHEST = 'CHEST',
  BACK = 'BACK',
  SHOULDERS = 'SHOULDERS',
  BICEPS = 'BICEPS',
  TRICEPS = 'TRICEPS',
  FOREARMS = 'FOREARMS',
  CORE = 'CORE',
  GLUTES = 'GLUTES',
  QUADS = 'QUADS',
  HAMSTRINGS = 'HAMSTRINGS',
  CALVES = 'CALVES',
  FULL_BODY = 'FULL_BODY',
  CARDIO = 'CARDIO',
}

export enum EquipmentKind {
  BODYWEIGHT = 'BODYWEIGHT',
  BARBELL = 'BARBELL',
  DUMBBELL = 'DUMBBELL',
  KETTLEBELL = 'KETTLEBELL',
  MACHINE = 'MACHINE',
  CABLE = 'CABLE',
  RESISTANCE_BAND = 'RESISTANCE_BAND',
  CARDIO_MACHINE = 'CARDIO_MACHINE',
  OTHER = 'OTHER',
}

export enum ExerciseDifficulty {
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
}

export type ExerciseStatus = 'ACTIVE' | 'ARCHIVED'
export type WorkoutTemplateStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

export interface ExerciseCategory {
  id: number
  name: string
  exerciseCount?: number
}

export interface Exercise {
  id: number
  /** Single free-language name — gyms name exercises however staff know them. */
  name: string
  categoryId: number
  muscleGroups: MuscleGroup[]
  equipment: EquipmentKind[]
  difficulty: ExerciseDifficulty
  instructions?: string
  imageUrls: string[]
  youtubeUrl?: string
  status: ExerciseStatus
  createdAt: string
  updatedAt: string
}

/** Denormalized read shape embedded in workout rows so lists render without N extra fetches. */
export interface ExerciseSummary {
  id: number
  name: string
  thumbnailUrl?: string
  /** Passed through so a session/member surface can embed the how-to video. */
  youtubeUrl?: string
  difficulty: ExerciseDifficulty
  muscleGroups: MuscleGroup[]
}

export interface WorkoutExercise {
  id: number
  exerciseId: number
  exercise?: ExerciseSummary
  order: number
  sets?: number
  /** String on purpose — trainers write "8-12", "AMRAP", "30s". */
  reps?: string
  restSeconds?: number
  /** String on purpose — "60kg", "BW", "70% 1RM". */
  weight?: string
  notes?: string
  /** Reserved for future superset pairing; same number = same group. No UI in v1. */
  supersetGroup?: number
}

export interface WorkoutSection {
  id: number
  title: string
  order: number
  exercises: WorkoutExercise[]
}

export interface WorkoutTemplate {
  id: number
  name: string
  description?: string
  difficulty: ExerciseDifficulty
  /** Auto-derived at save time from sections (see features/workout/utils/duration). */
  durationMinutes: number
  targetMuscleGroups: MuscleGroup[]
  status: WorkoutTemplateStatus
  sections: WorkoutSection[]
  createdAt: string
  updatedAt: string
}

/** List-card shape: no section tree, but enough to render the card collage. */
export interface WorkoutTemplateSummary {
  id: number
  name: string
  description?: string
  difficulty: ExerciseDifficulty
  durationMinutes: number
  targetMuscleGroups: MuscleGroup[]
  status: WorkoutTemplateStatus
  exerciseCount: number
  previewImageUrls: string[]
  updatedAt: string
}

export interface WorkoutPagination {
  page: number
  pageSize: number
  total: number
}

export interface WorkoutListResponse<T> {
  data: T[]
  pagination: WorkoutPagination
}

export interface ExerciseListParams {
  page?: number
  pageSize?: number
  search?: string
  categoryId?: number
  muscleGroup?: MuscleGroup
  equipment?: EquipmentKind
  difficulty?: ExerciseDifficulty
  status?: ExerciseStatus
}

export interface WorkoutTemplateListParams {
  page?: number
  pageSize?: number
  search?: string
  difficulty?: ExerciseDifficulty
  muscleGroup?: MuscleGroup
  status?: WorkoutTemplateStatus
}

export interface CreateExerciseBody {
  name: string
  categoryId: number
  muscleGroups: MuscleGroup[]
  equipment: EquipmentKind[]
  difficulty: ExerciseDifficulty
  instructions?: string
  imageUrls: string[]
  youtubeUrl?: string
}

export type UpdateExerciseBody = Partial<CreateExerciseBody>

export interface SaveWorkoutExerciseBody {
  exerciseId: number
  order: number
  sets?: number
  reps?: string
  restSeconds?: number
  weight?: string
  notes?: string
  supersetGroup?: number
}

export interface SaveWorkoutSectionBody {
  title: string
  order: number
  exercises: SaveWorkoutExerciseBody[]
}

export interface SaveWorkoutTemplateBody {
  name: string
  description?: string
  difficulty: ExerciseDifficulty
  durationMinutes: number
  status: Exclude<WorkoutTemplateStatus, 'ARCHIVED'>
  sections: SaveWorkoutSectionBody[]
}

// ─── Slice 2 contract (assignment) — defined now, not implemented ──────────

export interface WorkoutAssignment {
  id: number
  memberId: number
  templateId: number
  /** Frozen copy so later template edits don't rewrite a member's history. */
  templateSnapshot: WorkoutTemplate
  assignedBy: number
  startDate?: string
  endDate?: string
  daysOfWeek?: number[]
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED'
  notes?: string
  ptBookingId?: number
  goalSessionId?: number
  createdAt: string
}
